import { useEffect } from 'react';
import { APIError, ClientError, useErrorHandler, useQueryClient } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';

import { addStrefa } from 'api/strefy/strefy';
import { StrefyErrorCode } from 'api/strefy/strefy.enum';
import { StrefyErrorResponse } from 'api/strefy/strefy.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { useMutation } from 'hooks/useMutation/useMutation';
import { AppMessages } from 'i18n/messages';

import { AddZone } from './AddZone';
import { AddZoneContainerProps, AddZoneFormBody } from './AddZone.types';
import { useAddZoneValidation } from './AddZone.validation';

export const AddZoneContainer = ({
  parentZone,
  allZoneCodes,
  isModalOpen,
  onModalClose,
  onZoneAdded,
}: AddZoneContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { handleError } = useErrorHandler();
  const { schema } = useAddZoneValidation(allZoneCodes);

  const methods = useForm<AddZoneFormBody>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      kod: '',
      nazwa: '',
    },
  });

  const { mutate: onAddZone, isLoading } = useMutation(addStrefa, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyEnum.STREFY]);
      toast.dark(formatMessage({ id: AppMessages['definitions.zones.add.success'] }));
      onZoneAdded();
      onModalClose();
    },
    onError: (error: ClientError<APIError<StrefyErrorResponse>>) => {
      const code = error.response?.data.code;

      if (code === StrefyErrorCode.STREFA_KOD_DUPLIKAT) {
        methods.setError('kod', {
          message: formatMessage({ id: AppMessages['definitions.zones.add.code.duplicate'] }),
        });
        return;
      }

      if (code === StrefyErrorCode.MONITORING_AKTYWNY) {
        queryClient.invalidateQueries([QueryKeyEnum.MONITORING_OBECNOSCI]);
        toast.dark(formatMessage({ id: AppMessages['definitions.zones.monitoringActive'] }));
        onModalClose();
        return;
      }

      if (
        code === StrefyErrorCode.STREFA_NIE_ISTNIEJE ||
        code === StrefyErrorCode.STREFA_MAX_POZIOM ||
        code === StrefyErrorCode.STREFA_KORZEN_ZAJETY
      ) {
        queryClient.invalidateQueries([QueryKeyEnum.STREFY]);
      }

      handleError({ error });
    },
  });

  const onSubmit = ({ kod, nazwa }: AddZoneFormBody) => {
    onAddZone({ kod: kod.trim(), nazwa: nazwa.trim(), parentId: parentZone.id });
  };

  useEffect(() => {
    methods.reset();
  }, [isModalOpen]);

  return (
    <FormProvider {...methods}>
      <AddZone
        parentZone={parentZone}
        onSubmit={onSubmit}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        isLoading={isLoading}
      />
    </FormProvider>
  );
};
